import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { DataTableSelectionState } from 'components/data-table';
import type { SearchResult } from 'types/api-responses';
import { selectedEvalsAtom } from './atoms';
import {
  ORIGIN_EVALUATION_ID_PARAM,
  ORIGIN_EVAL_VERSION_PARAM,
  evalVersionToSource,
} from './utils';
import type { EvalUnifiedResult } from './types';

const rowKey = (e: EvalUnifiedResult) => `${e.evalId}_${e.source}`;

/**
 * Manages evaluation selection state.
 * Works with DataTable's built-in selectionType: 'multi' + maxSelections.
 * @param {EvalUnifiedResult[]} visibleData - Current visible rows in the DataTable
 * @return {object} Selection state and the row-select handler
 */
export const useEvalSelection = (visibleData: EvalUnifiedResult[]) => {
  const [selectedEvals, setSelectedEvals] = useAtom(selectedEvalsAtom);

  /**
   * Handles DataTable onRowSelect callback.
   * DataTable sends { selectAll, toggledNodes }. For client-paginated tables,
   * selectAll is always false and toggledNodes holds selected row IDs (in the
   * order they were toggled — insertion order defines Origin then Target).
   * @param {object} selection - { selectAll: boolean, toggledNodes: string[] }
   */
  const handleRowSelect = useCallback(
    (selection: DataTableSelectionState) => {
      const ids = selection.toggledNodes;
      const visibleMap = new Map(visibleData.map((e) => [rowKey(e), e]));
      setSelectedEvals((prev) => {
        const prevMap = new Map(prev.map((e) => [rowKey(e), e]));
        // Resolve each selected ID: from prev (already stored) or from visible data (new)
        return ids
          .map((id) => prevMap.get(id) ?? visibleMap.get(id))
          .filter((e): e is EvalUnifiedResult => e !== undefined);
      });
    },
    [setSelectedEvals, visibleData]
  );

  return {
    selectedEvals,
    handleRowSelect,
  };
};

/** The Origin (baseline) a wizard URL asks to pre-select, resolved to API values. */
interface OriginParams {
  evalId: number;
  source: string;
}

/**
 * The Origin from the URL's side_a_* params — e.g. a package's Actions →
 * "Package Compare", which links to
 * `?side_a_eval_version=eval_3&side_a_evaluation_id=<id>`. null when the params
 * are absent (opened from the packages list) or malformed.
 * @return {OriginParams | null} The parsed origin, or null
 */
const readOriginParams = (): OriginParams | null => {
  const params = new URLSearchParams(window.location.search);
  const evalVersion = params.get(ORIGIN_EVAL_VERSION_PARAM);
  const rawEvalId = params.get(ORIGIN_EVALUATION_ID_PARAM);
  if (!evalVersion || !rawEvalId) return null;

  const evalId = Number(rawEvalId);
  if (!Number.isInteger(evalId) || evalId <= 0) return null;

  return { evalId, source: evalVersionToSource(evalVersion) };
};

/**
 * Fetches the single evaluation row for the Origin. Uses the `eval_id_exact`
 * term filter (not the fuzzy `text` / `eval_id` substring match) so a low id
 * like "7" resolves to exactly that eval rather than every eval whose id
 * contains a 7. Returns the fully-shaped row (so the sidebar renders package
 * name + links), or null when it isn't found — e.g. not yet indexed — so the
 * wizard just opens empty.
 * @param {OriginParams} origin - The exact id + source to look up
 * @param {AbortSignal} signal - React Query's cancellation signal
 * @return {Promise<EvalUnifiedResult | null>} The matching row, or null
 */
const fetchOriginEval = async (
  origin: OriginParams,
  signal: AbortSignal
): Promise<EvalUnifiedResult | null> => {
  const { evalId, source } = origin;
  const data = await api
    .post('queries/package_evals_unified', {
      json: { eval_id_exact: evalId, source, page: 1, page_size: 1 },
      signal,
    })
    .json<SearchResult<EvalUnifiedResult>>();

  return (
    data.results.find((e) => e.evalId === evalId && e.source === source) ?? null
  );
};

/**
 * Co-locates the Origin lookup's key and fetcher. A null origin keys an inert
 * query (gated off by `enabled` at the call site), so the factory always returns
 * a well-formed queryKey.
 * @param {OriginParams | null} origin - The parsed origin, or null
 * @return The queryOptions for the Origin lookup
 */
const originEvalQueryOptions = (origin: OriginParams | null) =>
  queryOptions({
    queryKey: ['compare-origin-eval', origin],
    queryFn: ({ signal }) => (origin ? fetchOriginEval(origin, signal) : null),
  });

/**
 * Pre-selects the Origin (baseline) from the URL when the wizard is opened for a
 * specific evaluation. Fetches that exact evaluation once and seeds it as the
 * sole selection so the user only needs to pick the Target.
 *
 * No-ops when the params are absent (opened from the packages list) or malformed.
 * Never clobbers an existing selection restored from sessionStorage (returning via
 * the results "Edit" link), so it only seeds a genuinely empty wizard.
 */
export const useOriginAutopopulate = () => {
  const [selectedEvals, setSelectedEvals] = useAtom(selectedEvalsAtom);
  // The wizard's side_a_* params are fixed for its lifetime, so parse them once:
  // re-reading per render would hand the query a fresh key object each time.
  const [origin] = useState(readOriginParams);
  // Latch eligibility at mount: seed ONLY a wizard that opened empty, and only
  // once. `enabled` is recomputed every render, so gating on the LIVE
  // selectedEvals would re-enable the moment the user CLEARS the prefilled
  // Origin — refetching (staleTime is 0 by default) and, if the indexed row
  // changed, re-seeding the row they just removed. Reading the mount-time
  // emptiness instead makes the lookup a one-shot that can never put an Origin
  // back. staleTime Infinity keeps that one fetch from repeating. React Query
  // owns the request's abort-on-unmount.
  const [seedOnMount] = useState(() => selectedEvals.length === 0);

  const { data: originRow } = useQuery({
    ...originEvalQueryOptions(origin),
    enabled: origin !== null && seedOnMount,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!originRow) return;
    // The functional update re-reads the live selection and bails if the user
    // picked rows while the lookup was in flight, so it never clobbers them.
    setSelectedEvals((prev) => (prev.length > 0 ? prev : [originRow]));
  }, [originRow, setSelectedEvals]);
};
